import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import i18n from '@/i18n';

const PRODUCTION_SITE_URL = 'https://tulasihome.com';
const CONFIGURED_SITE_URL = (import.meta.env.VITE_SITE_URL ?? PRODUCTION_SITE_URL).replace(/\/+$/, '');
const DEFAULT_IMAGE_PATH = '/tulasi-circle.png';
const BRAND_NAME = 'Tulasi Home';
const DEFAULT_TITLE = 'Tulasi Home | Fine Art Printing Studio for Artists & Photographers';
const DEFAULT_DESCRIPTION = 'Order museum-quality fine art prints on paper and canvas. Tulasi Home helps artists and photographers prepare, print, package, and ship artwork.';

type StructuredData = Record<string, unknown> | ReadonlyArray<Record<string, unknown>>;

type SeoConfig = {
  readonly title: string;
  readonly description: string;
  readonly robots: string;
  readonly canonicalPath: string;
  readonly ogType: 'website' | 'article';
  readonly imagePath?: string;
  readonly structuredData: StructuredData;
};

const getSiteUrl = (): string => {
  if (typeof window === 'undefined') {
    return CONFIGURED_SITE_URL;
  }

  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return origin;
  }

  return CONFIGURED_SITE_URL;
};

const normalizePath = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : '/';
};

const mapLanguageToOgLocale = (language: string): string => {
  switch (language) {
    case 'uk':
      return 'uk_UA';
    case 'es':
      return 'es_ES';
    case 'fr':
      return 'fr_FR';
    case 'pt':
      return 'pt_PT';
    case 'ru':
      return 'ru_RU';
    case 'it':
      return 'it_IT';
    case 'de':
      return 'de_DE';
    default:
      return 'en_US';
  }
};

const getBaseStructuredData = (siteUrl: string, language: string) => {
  const imageUrl = `${siteUrl}${DEFAULT_IMAGE_PATH}`;

  return {
    organization: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}#organization`,
      name: BRAND_NAME,
      url: siteUrl,
      logo: imageUrl,
      image: imageUrl,
      email: 'contacts@tulasihome.com',
      description: DEFAULT_DESCRIPTION,
    },
    website: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}#website`,
      url: siteUrl,
      name: BRAND_NAME,
      inLanguage: language,
      publisher: {
        '@id': `${siteUrl}#organization`,
      },
    },
  };
};

const buildWebPageSchema = (
  siteUrl: string,
  language: string,
  config: Pick<SeoConfig, 'title' | 'description' | 'canonicalPath'>,
  pageType: string = 'WebPage',
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': pageType,
  '@id': `${siteUrl}${config.canonicalPath}#webpage`,
  url: `${siteUrl}${config.canonicalPath}`,
  name: config.title,
  description: config.description,
  inLanguage: language,
  isPartOf: {
    '@id': `${siteUrl}#website`,
  },
  about: {
    '@id': `${siteUrl}#organization`,
  },
});

const buildServiceSchema = (
  siteUrl: string,
  language: string,
  config: Pick<SeoConfig, 'title' | 'description' | 'canonicalPath'>,
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}${config.canonicalPath}#service`,
  serviceType: 'Fine art printing and artisan production',
  name: config.title,
  description: config.description,
  provider: {
    '@id': `${siteUrl}#organization`,
  },
  areaServed: 'Worldwide',
  availableChannel: {
    '@type': 'ServiceChannel',
    serviceUrl: `${siteUrl}${config.canonicalPath}`,
  },
  inLanguage: language,
});

const getSeoConfig = (pathname: string, language: string): SeoConfig => {
  const siteUrl = getSiteUrl();
  const base = getBaseStructuredData(siteUrl, language);
  const path = normalizePath(pathname);

  const page = (
    config: Omit<SeoConfig, 'structuredData'> & { readonly structuredData?: StructuredData },
  ): SeoConfig => ({
    ...config,
    structuredData: config.structuredData ?? [
      base.organization,
      base.website,
      buildWebPageSchema(siteUrl, language, config),
    ],
  });

  if (path.startsWith('/admin')) {
    return page({
      title: `${BRAND_NAME} Admin`,
      description: 'Administrative workspace.',
      robots: 'noindex, nofollow, noarchive',
      canonicalPath: path,
      ogType: 'website',
    });
  }

  switch (path) {
    case '/':
      return page({
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        robots: 'index, follow, max-image-preview:large',
        canonicalPath: '/',
        ogType: 'website',
        structuredData: [
          base.organization,
          base.website,
          buildWebPageSchema(siteUrl, language, {
            title: DEFAULT_TITLE,
            description: DEFAULT_DESCRIPTION,
            canonicalPath: '/',
          }),
          buildServiceSchema(siteUrl, language, {
            title: 'Fine Art Printing & Canvas Printing Services',
            description: DEFAULT_DESCRIPTION,
            canonicalPath: '/',
          }),
        ],
      });
    case '/studio':
      return page({
        title: 'Art Print Order Studio | Upload, Size, Paper & Printer Setup',
        description: 'Prepare fine art print orders online: upload artwork, choose print size, paper, printer, shipping, and payment in one guided studio flow.',
        robots: 'index, follow, max-image-preview:large',
        canonicalPath: '/studio',
        ogType: 'website',
        structuredData: [
          base.organization,
          base.website,
          buildWebPageSchema(siteUrl, language, {
            title: 'Art Print Order Studio | Upload, Size, Paper & Printer Setup',
            description: 'Prepare fine art print orders online: upload artwork, choose print size, paper, printer, shipping, and payment in one guided studio flow.',
            canonicalPath: '/studio',
          }),
          buildServiceSchema(siteUrl, language, {
            title: 'Art Print Order Studio',
            description: 'Online workflow for configuring and ordering fine art paper and canvas prints.',
            canonicalPath: '/studio',
          }),
        ],
      });
    case '/about':
      return page({
        title: 'About Tulasi Home | Fine Art Printing Studio',
        description: 'Learn about Tulasi Home, our fine art printing standards, papers, pigment inks, packaging care, and our approach to museum-quality art reproduction.',
        robots: 'index, follow, max-image-preview:large',
        canonicalPath: '/about',
        ogType: 'article',
      });
    case '/contact':
      return page({
        title: 'Contact Tulasi Home | Art Print Support',
        description: 'Contact Tulasi Home for fine art printing, paper and canvas print support, order help, and artist workflow questions.',
        robots: 'index, follow',
        canonicalPath: '/contact',
        ogType: 'website',
        structuredData: [
          base.organization,
          base.website,
          buildWebPageSchema(siteUrl, language, {
            title: 'Contact Tulasi Home | Art Print Support',
            description: 'Contact Tulasi Home for fine art printing, paper and canvas print support, order help, and artist workflow questions.',
            canonicalPath: '/contact',
          }, 'ContactPage'),
        ],
      });
    case '/pricing':
      return page({
        title: 'Fine Art Print Pricing | Paper & Canvas Cost Calculator',
        description: 'Estimate fine art printing prices across print sizes, papers, discounts, and currencies with Tulasi Home’s online pricing calculator.',
        robots: 'index, follow, max-image-preview:large',
        canonicalPath: '/pricing',
        ogType: 'website',
        structuredData: [
          base.organization,
          base.website,
          buildWebPageSchema(siteUrl, language, {
            title: 'Fine Art Print Pricing | Paper & Canvas Cost Calculator',
            description: 'Estimate fine art printing prices across print sizes, papers, discounts, and currencies with Tulasi Home’s online pricing calculator.',
            canonicalPath: '/pricing',
          }),
          buildServiceSchema(siteUrl, language, {
            title: 'Fine Art Print Pricing Calculator',
            description: 'Interactive calculator for paper and canvas art print pricing.',
            canonicalPath: '/pricing',
          }),
        ],
      });
    case '/discounts':
      return page({
        title: 'Artist Discounts & Referral Programs | Tulasi Home',
        description: 'Explore artist discounts, creator programs, referral rewards, and partnership benefits for Tulasi Home fine art printing customers.',
        robots: 'index, follow, max-image-preview:large',
        canonicalPath: '/discounts',
        ogType: 'website',
      });
    case '/oferta':
      return page({
        title: 'Public Offer & Terms | Tulasi Home',
        description: 'Read the Tulasi Home public offer, ordering terms, delivery details, and legal information for art printing services.',
        robots: 'index, follow',
        canonicalPath: '/oferta',
        ogType: 'article',
      });
    case '/integrations/ai-artist':
      return page({
        title: 'AI Artist Integration | Tulasi Home',
        description: 'Learn how Tulasi Home supports AI artist workflows and future artwork handoff integrations for print production.',
        robots: 'index, follow',
        canonicalPath: '/integrations/ai-artist',
        ogType: 'website',
      });
    case '/integrations':
    case '/integrations/etsy':
    case '/integrations/etsy/orders':
    case '/integrations/etsy/matching':
    case '/wallet':
    case '/confirm-email':
    case '/forgot-password':
    case '/reset-password':
      return page({
        title: `${BRAND_NAME}`,
        description: DEFAULT_DESCRIPTION,
        robots: 'noindex, nofollow, noarchive',
        canonicalPath: path,
        ogType: 'website',
      });
    default:
      return page({
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        robots: 'noindex, follow',
        canonicalPath: path,
        ogType: 'website',
      });
  }
};

const setMeta = (selector: string, attributeName: 'name' | 'property', attributeValue: string, content: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const setStructuredData = (value: StructuredData) => {
  if (typeof document === 'undefined') {
    return;
  }

  let script = document.head.querySelector<HTMLScriptElement>('script[data-seo="structured-data"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seo = 'structured-data';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(value);
};

const applySeo = (pathname: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const siteUrl = getSiteUrl();
  const language = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const seo = getSeoConfig(pathname, language);
  const canonicalUrl = `${siteUrl}${seo.canonicalPath}`;
  const imageUrl = `${siteUrl}${seo.imagePath ?? DEFAULT_IMAGE_PATH}`;

  document.title = seo.title;
  setMeta('meta[name="description"]', 'name', 'description', seo.description);
  setMeta('meta[name="robots"]', 'name', 'robots', seo.robots);
  setMeta('meta[name="googlebot"]', 'name', 'googlebot', seo.robots);
  setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', BRAND_NAME);
  setMeta('meta[property="og:type"]', 'property', 'og:type', seo.ogType);
  setMeta('meta[property="og:title"]', 'property', 'og:title', seo.title);
  setMeta('meta[property="og:description"]', 'property', 'og:description', seo.description);
  setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
  setMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
  setMeta('meta[property="og:locale"]', 'property', 'og:locale', mapLanguageToOgLocale(language));
  setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
  setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
  setLink('canonical', canonicalUrl);
  setStructuredData(seo.structuredData);
};

export const usePageSeo = () => {
  const location = useLocation();

  useEffect(() => {
    const handleLanguageChange = () => {
      applySeo(location.pathname);
    };

    applySeo(location.pathname);
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [location.pathname]);
};
